import { useMemo } from 'react'
import { CellDataField, GROUP_RUN_INFORMATION, getDataFields } from 'redux/slices'
import { useAuth } from 'services/auth'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { CellInfo } from './tsv/types'
import { getCustomFields } from './utils'
import { getColumn } from './arrow/arqueroHelpers'

/**
 * Filters list of available data fields present in the dataset.
 * And also filters fields that for internal vs. external users
 *
 * Try to use this hook for any purpose where you need a
 *
 * @TODO Currently this is based on a hard-coded schema from getDataFields()
 * Change this to depend on a schema defined in the session later
 *
 * @returns Data fields in the dataset
 * {
 *      dataFields: DataField[] // all data fields
 *      quantitativeDataFields: DataField[] // all data fields that are quantitative (i.e. continuous real variables)
 *      categoricalDataFields: DataField[] // all data fields that are categorical (i.e. a list of items)
 * }
 */
const useDataFieldsInDataset = (): {
  dataFields: CellDataField[]
  quantitativeDataFields: CellDataField[]
  categoricalDataFields: CellDataField[]
} => {
  const { isInternalUser } = useAuth()
  const { dataFrame, getCellInfoByCellId } = useCellVisualizationsSlice()

  const dataFields: CellDataField[] = useMemo(() => {
    const allDataFields = getDataFields()

    if (!dataFrame) return []

    // Get column names from DataFrame
    const columnNames = dataFrame.columnNames()
    if (!columnNames || columnNames.length === 0) return []

    const customFields = getCustomFields(columnNames)

    // Get first cell to check for non-empty values using getColumn helper
    const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
    if (cellIds.length === 0) return []

    const firstCell = getCellInfoByCellId(cellIds[0])

    const allCellDataFields = allDataFields.flatMap((option: CellDataField) => {
      const output: CellDataField[] = []
      // Check if the feature is internal only and the user is not internal
      if (option.isInternalOnly && !isInternalUser) {
        return output
      }

      // Only keep features that are in the data and have non-empty values
      const attribute = option.attribute as keyof CellInfo

      if (attribute in firstCell) {
        const value = firstCell[attribute]
        if (value !== undefined && value !== '') {
          output.push(option)
        }
      }
      return output
    })

    const lastRunInfoIndex = allCellDataFields.reduce(
      (acc, cdf, i) => (cdf.category === GROUP_RUN_INFORMATION ? i : acc),
      -1
    )
    if (lastRunInfoIndex > -1) {
      allCellDataFields.splice(lastRunInfoIndex + 1, 0, ...customFields)
    } else {
      // if Run Information isn't in there, just put it at the beginning
      allCellDataFields.splice(0, 0, ...customFields)
    }

    return allCellDataFields
  }, [dataFrame, getCellInfoByCellId, isInternalUser])

  return {
    dataFields,
    quantitativeDataFields: dataFields.filter((option) => option.isContinuous),
    categoricalDataFields: dataFields.filter((option) => !option.isContinuous),
  }
}

export default useDataFieldsInDataset
